<template>
	<div>
		<div class="d-flex flex-row align-items-center justify-content-between">
			<h3 v-if="!isWizardInstance">Charge Out Groups</h3>

			<button v-on:click="newGroup" class="btn btn-success">
				<i class="fa fa-plus"></i> 
				New Charge Out Group
			</button>
		</div>

		<onboarding-card v-if="!isWizardInstance" title="Set charge out groups" name="Charge Out Groups" setting-key="classificationGroups">
			<p>Often you may need to bill out multiple employee classifications at a single rate. Setting up Charge Out Groups allows you to assign multiple classifications to a single group, and set a default billing rate for that group.</p>
			<p>Billing rates for charge out groups can also be over-ridden per customer in Contact invoice settings.</p>
		</onboarding-card>
			
		<table-list
			:records="mutatedGroups"
			:mobile-breakpoint="$store.tableListMobileBreakpoint"
			:fields="mutatedFields"
			record-name="Group"
			:has-edit="true"
			id-field="slug"
			v-on:edit="editRecord">
		</table-list>

		<modal-dialog
			:modal-visible="editModal.visible"
			:title="editModal.title"
			confirm-button-text="Save"
			v-on:confirm="save"
			v-on:closeModal="editModal.visible = false">

			<field label="Group Label" col-class="col-5 col-sm-6" v-model="editModal.payload.label" ></field>

			<field 
				label="Charge Out Rate" 
				col-class="col-sm-6 col-7" 
				v-model="editModal.payload.charge_out_rate" 
				:params="{type: 'number', step: 0.01, min: 0}"
				v-on:change="mainRateChange">
			</field>

			<field 
				v-if="timeLogSettings.uses_overtime_15x == 1"
				label="OT 1.5x Charge Out Rate" 
				col-class="col-sm-6 col-7" 
				v-model="editModal.payload.ot_charge_out_rate" 
				:params="{type: 'number', step: 0.01, min: 0}">
			</field>

			<field 
				v-if="timeLogSettings.uses_overtime_2x == 1"
				label="OT 2x Charge Out Rate" 
				col-class="col-sm-6 col-7" 
				v-model="editModal.payload.ot2x_charge_out_rate" 
				:params="{type: 'number', step: 0.01, min: 0}">
			</field>

			<field 
				v-if="timeLogSettings.uses_overtime_25x == 1"
				label="OT 2.5x Charge Out Rate" 
				col-class="col-sm-6 col-7" 
				v-model="editModal.payload.ot25x_charge_out_rate" 
				:params="{type: 'number', step: 0.01, min: 0}">
			</field>

			<div class="row" v-if="editModal.showDelete">
				<div class="col-sm-3" style="padding-top: 3px;">

					<button type="button" v-if="editModal.record.EmployeeClassification && editModal.record.EmployeeClassification.length > 0" class="btn btn-outline-danger float-left" disabled>
						<i class="fa fa-trash"></i> Delete
					</button>

					<button type="button" v-else class="btn btn-outline-danger float-left" v-on:click="deleteItem"><i class="fa fa-trash"></i> Delete</button>
					
				</div>
				<div v-if="editModal.record.EmployeeClassification && editModal.record.EmployeeClassification.length > 0" class="col-sm-9 text-muted">
					Charge Out Groups may only be deleted if no Employee Classification has been assigned to it.
				</div>
			</div>

		</modal-dialog>

	</div>
</template>

<script>
import FormGroup from "@/components/FormGroup"
import Field from "@/components/Field"
import TableList from "@/components/TableList"
import ModalDialog from "@/components/ModalDialog"

import OnboardingCard from "@/components/OnboardingCard"

export default {

	name: 'ChargeOutGroups',
	components: {
		TableList,
		ModalDialog,
		FormGroup,
		Field,
		OnboardingCard
	},
	props:["isWizardInstance"],
	data() {
		return {
			fields: [
				{
					label: "Group Name",
					key: "label"
				},
				{
					label: "# of Classifications in Group",
					key: "numClassifications",
					showOnMobile: false
				},
				{
					label: "Reg. Charge Out",
					key: "charge_out_rate"
				}
			],
			groups: [],
			timeLogSettings: [],
			editModal: {
				visible: false,
				title: "Edit Charge Out Group",
				payload: {
					label: "",
					charge_out_rate: null,
					ot_charge_out_rate: null,
					ot2x_charge_out_rate: null,
					ot25x_charge_out_rate: null
				},
				record: {},
				showDelete: true
			}
		}
	},
	computed: {
		mutatedFields() {
			let fields = JSON.parse(JSON.stringify(this.fields))

			if (this.timeLogSettings.uses_overtime_15x == 1) {
				fields.push({
					label: "OT 1.5x Charge Out",
					key: "ot_charge_out_rate"
				})
			}

			if (this.timeLogSettings.uses_overtime_2x == 1) {
				fields.push({
					label: "OT 2x Charge Out",
					key: "ot2x_charge_out_rate"
				})
			}

			if (this.timeLogSettings.uses_overtime_25x == 1) {
				fields.push({
					label: "OT 2.5x Charge Out",
					key: "ot25x_charge_out_rate"
				})
			}

			return fields
		},
		mutatedGroups() {
			let vm = this

			return this.groups.map(group => {
				group.numClassifications = group.EmployeeClassification.length

				return group
			})
		}
	},
	methods: {
		mainRateChange() {
			if (this.editModal.payload.charge_out_rate > 0) {
				if (!this.editModal.payload.ot_charge_out_rate)
					this.editModal.payload.ot_charge_out_rate = +this.editModal.payload.charge_out_rate * 1.5

				if (!this.editModal.payload.ot2x_charge_out_rate)
					this.editModal.payload.ot2x_charge_out_rate = +this.editModal.payload.charge_out_rate * 2

				if (!this.editModal.payload.ot25x_charge_out_rate)
					this.editModal.payload.ot25x_charge_out_rate = +this.editModal.payload.charge_out_rate * 2.5
			}
		},
		newGroup() {
			this.editModal.payload = {}
			this.editModal.visible = true
			this.editModal.title = "New Charge Out Group"
			this.editModal.record = {}
			this.editModal.showDelete = false
		},
		editRecord(record) {
			this.editModal.payload = record
			this.editModal.visible = true
			this.editModal.title = "Edit Charge Out Group"
			this.editModal.record = record
			this.editModal.showDelete = true
		},
		save() {
			let vm = this
			this.saveRecord("ClassificationGroup",this.editModal.payload,function(){
				vm.editModal.visible = false
				vm.alertSaved("Group saved!")
				vm.fetchData()
				vm.editModal.payload = {}
			})
		},
		deleteItem() {
			let vm = this

			this.deleteRecord('ClassificationGroup',this.editModal.record.id,function(){
				vm.editModal.visible = false;
				vm.alertSuccess("Group deleted!")
				vm.fetchData()
			})
		},
		fetchData() {
			let vm = this

			let includes = JSON.stringify(["EmployeeClassification"])

			let promises = [
				this.getJSON("ClassificationGroup/get?include="+includes),
				this.getJSON("TimeLogSetting/get")
			]

			this.spinner(true)

			Promise.all(promises).then(responses => {
				vm.groups = responses[0].data

				vm.timeLogSettings = responses[1].data[0]

				vm.spinner(false)
			})
		}
	},
	created() {
		this.fetchData()

		let vm = this
		//list for new record button click event from parent
		this.$bus.$on("newClassificationGroup", function(){
			vm.editModal.title = "New Charge Out Group"
			vm.editModal.payload = {
				label: "",
				charge_out_rate: null,
				ot_charge_out_rate: null,
				ot2x_charge_out_rate: null,
				ot25x_charge_out_rate: null
			}
			vm.editModal.visible = true
			vm.editModal.showDelete = false
		})
	}

}
</script>

